import type { DataGridCheckRow } from '../DataGrid/CheckCell/CheckCell';
import type { PageChangeFn } from '../Pagination/Pagination';

import { use, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { EMPTY_FUNCTION } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import UserContext from '../../contexts/UserContext';

import {
  BoxWithArrowIcon,
  DownloadCircleIcon,
  PencilSolidIcon,
  RefreshIcon,
  TrashSolidIcon,
} from '../../images/shapes';

import Button from '../Button/Button';
import Pagination from '../Pagination/Pagination';
import ResultsPerPageLabel from '../Pagination/ResultsPerPageLabel';
import DuplicateSelectedButton from './buttons/DuplicateSelectedButton';
import MassActionButton from './buttons/MassActionButton';
import StatusbarActionMenu from './buttons/StatusbarActionMenu';

interface StatusBarProps {
  /**
   * Number of rows
   */
  rows?: number;
  /**
   * Array of checked rows ids
   */
  checkedRows?: DataGridCheckRow[];
  /**
   * Content type name
   */
  contentTypeName?: string;
  /**
   * Open modal function
   */
  handleDeleteClick?: () => void;
  /**
   * Open mass edit modal
   */
  handleMassEditClick?: () => void;
  /**
   * Handle mass publish button click
   */
  handleMassPublishClick?: () => void;
  /**
   * Handle mass unpublishing button click
   */
  handleMassUnpublishClick?: () => void;
  /**
   * Handle mass archive button click
   */
  handleMassArchiveClick?: () => void;
  /**
   * Handle duplicate button click, if not provided, duplicate button will navigate to duplicate page
   */
  handleDuplicateClick?: (id: DataGridCheckRow) => Promise<void>;
  /**
   * Current page number
   */
  currentPage?: number;
  /**
   * Number of pages
   */
  pagesCount?: number;
  /**
   * Page change function
   */
  handlePageChange?: PageChangeFn;
  /**
   * Data update function
   */
  handleDataUpdate?: () => void;
  /**
   * If status bar should be more compact for small devices
   */
  compactForSmall?: boolean;
  /**
   * Visible results interval from
   */
  resultsFrom?: number;
  /*
   * Visible results interval to
   */
  resultsTo?: number;
  /*
   * Results total count
   */
  resultsTotalCount?: number;
  /**
   * Is status bar disabled
   */
  disabled?: boolean;
  /**
   * Handle reset grid
   */
  handleResetGrid?: () => void;
  /*
   * Limit reached info text
   */
  limitReached?: string;
  /**
   * If the content type is draft public
   */
  isDraftPublic?: boolean;
  /**
   * Test id for layout
   */
  testId?: string;
}

const StatusBar = ({
  rows = 0,
  checkedRows,
  contentTypeName,
  handleDeleteClick = EMPTY_FUNCTION,
  handleMassEditClick = EMPTY_FUNCTION,
  handleMassPublishClick = EMPTY_FUNCTION,
  handleMassUnpublishClick = EMPTY_FUNCTION,
  handleMassArchiveClick = EMPTY_FUNCTION,
  handleDuplicateClick,
  currentPage = 1,
  pagesCount,
  handlePageChange,
  handleDataUpdate = EMPTY_FUNCTION,
  compactForSmall = false,
  resultsFrom,
  resultsTo,
  resultsTotalCount,
  disabled = false,
  handleResetGrid,
  limitReached = '',
  isDraftPublic = false,
  testId = '',
}: StatusBarProps) => {
  const { t } = useTranslation();
  const { permissions } = use(UserContext);
  const { canUpdate, canCreate, canDelete } = useMemo(
    () =>
      contentTypeName
        ? permissions.getCoPermissions(contentTypeName)
        : { canUpdate: true, canCreate: true, canDelete: true },
    [contentTypeName, permissions],
  );

  return (
    <div
      className={twMerge(
        'flex items-center bg-white dark:bg-slate-950 justify-between h-[40px] md:h-[72px] text-slate-900',
        'dark:text-slate-200 rounded-b-lg border border-slate-100 dark:border-slate-800 border-t-0',
        'text-xs md:text-sm 2xl:text-base',
        compactForSmall ? 'px-2 lg:px-7' : 'px-4 xs:px-7',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      <div className="inline-flex items-center space-x-2 xs:space-x-4">
        {checkedRows && checkedRows?.length > 0 && (
          <StatusbarActionMenu testId={testId}>
            {checkedRows?.length === 1 && handleDuplicateClick && (
              <DuplicateSelectedButton
                checkedRows={checkedRows}
                limitReached={limitReached}
                actionDisallowed={!canCreate}
                onClick={handleDuplicateClick}
                testId={testId}
              />
            )}
            <MassActionButton
              checkedRows={checkedRows}
              handleClick={handleMassEditClick}
              buttonMessage={t('ObjectsOfType.MassEditCount', {
                editCount: checkedRows.length,
              })}
              color="blue"
              icon={<PencilSolidIcon className="w-3" />}
              disabled={disabled}
              actionDisallowed={!canUpdate}
              {...getTestProps(testId, 'mass-edit-data', 'testId')}
            />
            {isDraftPublic && (
              <>
                <MassActionButton
                  checkedRows={checkedRows}
                  handleClick={handleMassPublishClick}
                  buttonMessage={t('ObjectsOfType.MassPublishCount', {
                    publishCount: checkedRows.length,
                  })}
                  color="green"
                  icon={<DownloadCircleIcon className="w-3 rotate-180" />}
                  disabled={disabled}
                  actionDisallowed={!canUpdate}
                  {...getTestProps(testId, 'publish-data', 'testId')}
                />
                <MassActionButton
                  checkedRows={checkedRows}
                  handleClick={handleMassUnpublishClick}
                  buttonMessage={t('ObjectsOfType.MassUnpublishCount', {
                    unpublishCount: checkedRows.length,
                  })}
                  color="blue"
                  icon={<DownloadCircleIcon className="w-3" />}
                  disabled={disabled}
                  actionDisallowed={!canUpdate}
                  {...getTestProps(testId, 'unpublish-data', 'testId')}
                />

                <MassActionButton
                  checkedRows={checkedRows}
                  handleClick={handleMassArchiveClick}
                  buttonMessage={t('ObjectsOfType.MassArchiveCount', {
                    archiveCount: checkedRows.length,
                  })}
                  color="red"
                  icon={<BoxWithArrowIcon className="w-3" />}
                  disabled={disabled}
                  actionDisallowed={!canUpdate}
                  {...getTestProps(testId, 'archive-data', 'testId')}
                />
              </>
            )}
            <MassActionButton
              checkedRows={checkedRows}
              handleClick={handleDeleteClick}
              buttonMessage={t('ObjectsOfType.DeleteCount', {
                deleteCount: checkedRows.length,
              })}
              color="red"
              icon={<TrashSolidIcon className="w-3" />}
              disabled={disabled}
              actionDisallowed={!canDelete}
              {...getTestProps(testId, 'delete-data', 'testId')}
            />
          </StatusbarActionMenu>
        )}
        <span
          className={twMerge(
            compactForSmall
              ? 'hidden md:inline lg:text-sm 2xl:text-base md:w-fit'
              : 'lg:text-sm 2xl:text-base',
          )}
        >
          <Trans
            i18nKey="ObjectsOfType.Rows"
            values={{ rows }}
            components={{ 1: <p className="inline font-bold" /> }}
          />
        </span>
        {checkedRows && (
          <span>
            <Trans
              i18nKey="ObjectsOfType.Selected"
              values={{ selectedCount: checkedRows.length }}
              components={{ 1: <p className="inline font-bold" /> }}
            />
          </span>
        )}
        {handleResetGrid && (
          <Button
            buttonSize="sm"
            iconImage={<RefreshIcon className="text-blue w-4" />}
            onClick={handleResetGrid}
            buttonColor="borderless"
            {...getTestProps(testId, 'grid-reset', 'testId')}
          >
            {t('Global.ResetGrid')}
          </Button>
        )}
      </div>
      <div
        className={twMerge(
          'inline-flex items-center space-x-1 lg:space-x-4 2xl:space-x-8',
          disabled && 'pointer-events-none',
        )}
      >
        {!checkedRows?.length && (
          <ResultsPerPageLabel
            resultsIntervalFrom={resultsFrom}
            resultsIntervalTo={resultsTo}
            resultsTotalCount={resultsTotalCount}
            additionalClasses="p-0! w-0 hidden md:inline md:text-sm md:w-fit"
            {...getTestProps(testId, 'results-per-page-label', 'testId')}
          />
        )}
        {handlePageChange && (
          <Pagination
            page={currentPage}
            numOfPages={pagesCount}
            onPageChange={handlePageChange}
            additionalClasses="p-0!"
            {...getTestProps(testId, 'pagination', 'testId')}
          />
        )}
        <Button
          buttonSize="sm"
          iconImage={<RefreshIcon className="text-blue w-3.5" />}
          onClick={handleDataUpdate}
          buttonColor={'borderless'}
          additionalClasses={twMerge(
            '2xl:text-base dark:text-slate-200',
            disabled &&
              'bg-transparent hover:bg-transparent active:bg-transparent',
          )}
          additionalIconClasses="h-4 w-4"
          additionalChildrenClasses="hidden md:inline"
          disabled={disabled}
          {...getTestProps(testId, 'reload-data', 'testId')}
        >
          {t('ObjectsOfType.ReloadData')}
        </Button>
      </div>
    </div>
  );
};

export default StatusBar;
